import { Alert, AlertIcon, Box, Button, Flex, List, ListItem } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import useMobile from '../../hooks/utility/useMobile'


interface Props {
  error: string
  onRetry: () => void
  errorDetails?: { policy_violations?: string[] }
}

const AnswerErrorComponent: React.FC<Props> = ({ error, onRetry, errorDetails }) => {
  const isMobile = useMobile()
  const { t } = useLanguage()

  const displayMessage = useMemo(() => {
    let baseMessage = '';

    // If we have an error code, use it to get a translated message
    if (error) {
      switch(error) {
        case 'BOT_RESPONSE_CONTENT_VIOLATION':
          baseMessage = t('errors.assistantResponseContentViolation');
          break;
        case 'LLM_PROVIDER_BOT_RESPONSE_BLOCKED':
          baseMessage = t('errors.llmProviderBotResponseBlocked');
          break;
        case 'LLM_PROVIDER_API_ERROR':
          baseMessage = t('errors.llmProviderApiError');
          break;
        case 'MODEL_RATE_LIMITED':
          baseMessage = t('errors.modelRateLimited');
          break;
        case 'MODEL_TIMEOUT':
          baseMessage = t('errors.modelTimeout');
          break;
        // A model that is down or erroring is the same story for the reader:
        // not your fault, retry, someone else will answer.
        case 'MODEL_SERVER_ERROR':
        case 'MODEL_UNAVAILABLE':
          baseMessage = t('errors.modelUnavailable');
          break;
        case 'SERVER_ERROR':
          baseMessage = t('errors.serverError');
          break;
        case 'INTERNET_SEARCH_NOT_WAITING':
          baseMessage = t('errors.internetSearchNotWaiting');
          break;
        default:
          // If we have an unknown error code, fall back to the error message
          baseMessage = error;
      }
    } else {
      // Fallback to error message or a generic error
      baseMessage = error;
    }

    // Concatenate violations text if there are violations
    const violations = errorDetails?.policy_violations || [];
    if (violations.length > 0) {
      baseMessage += ` ${t('errors.startViolatedPoliciesList')}:`;
    }

    return baseMessage;
  }, [error, t, errorDetails])

  const hasRetryButton = error !== 'BOT_RESPONSE_CONTENT_VIOLATION' && error !== 'LLM_PROVIDER_BOT_RESPONSE_BLOCKED'
  const violations = errorDetails?.policy_violations || []

  return (
    <Alert
      status="error"
      borderRadius="md"
      mb={2}
      maxWidth={['80%', '80%', '70%']}
      minHeight="50px"
      alignItems="flex-start"
    >
      <AlertIcon />
      <Box flex="1">
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Box color="gray.700" fontSize={isMobile ? 'sm' : 'md'} flex="1" display="flex" alignItems="center">
            {displayMessage}
          </Box>
          {hasRetryButton && (
            <Button
              size="sm"
              borderRadius="full"
              boxShadow="sm"
              bg="secondary"
              color="white"
              border="1px solid"
              px={7}
              py={3}
              flexShrink={0}
              ml={2}
              _hover={{
                bg: 'white',
                color: 'secondary',
                border: '1px solid',
                borderColor: 'secondary',
              }}
              onClick={onRetry}
            >
              {t('common.tryAgain')}
            </Button>
          )}
        </Flex>
                  {violations.length > 0 && (
            <Box mt={2}>
              <List spacing={1} styleType="disc" ml={6}>
                {violations.map((violation, index) => (
                  <ListItem key={index} fontSize="sm" color="gray.700">
                    {violation}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
      </Box>
    </Alert>
  )
}

AnswerErrorComponent.displayName = 'AnswerError'

export const AnswerError = React.memo(AnswerErrorComponent)
